import { Link } from "react-router-dom";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import Icon from "@mui/material/Icon";
import iconHands from "assets/images/icons/icon-hands.png";

// Paletă aliniată cu restul site-ului (navy închis + accent galben).
const PANEL_BG = "#061D36";
const PANEL_BORDER = "rgba(255,255,255,0.05)";
const ACCENT_YELLOW = "#FFC107";
const ACCENT_YELLOW_HOVER = "#FFB300";
const TEXT_PRIMARY = "#ffffff";
const TEXT_SECONDARY = "rgba(255,255,255,0.78)";

/**
 * Shield outlined cu bifă centrală. SVG inline ca să avem control total asupra
 * aspectului (linie subțire aurie, bifă aurie în interior — exact ca în
 * referința de design).
 */
function ShieldCheck({ size = 96 }) {
  return (
    <SoftBox
      component="svg"
      viewBox="0 0 64 64"
      fill="none"
      aria-hidden="true"
      sx={{
        width: `${size}px`,
        height: `${size}px`,
        display: "block",
        flexShrink: 0,
      }}
    >
      <path
        d="M32 4 L56 14 V30 C56 44 46 54 32 60 C18 54 8 44 8 30 V14 Z"
        stroke={ACCENT_YELLOW}
        strokeWidth="3"
        strokeLinejoin="round"
        fill="none"
      />
      <path
        d="M22 32 L29 39 L43 24"
        stroke={ACCENT_YELLOW}
        strokeWidth="3.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </SoftBox>
  );
}

/**
 * Decor: mâini ridicate cu frunze — fundal discret în zona de jos a homepage-ului.
 * Folosit și în CallToAction; poate fi afișat separat când bannerul CTA e dezactivat.
 */
export function HandsBackgroundDecor() {
  return (
    <SoftBox
      aria-hidden="true"
      component="img"
      src={iconHands}
      alt=""
      sx={{
        position: "absolute",
        right: { md: "-150px" },
        top: { md: "-420px" },
        width: { md: "640px" },
        height: "auto",
        pointerEvents: "none",
        userSelect: "none",
        zIndex: 0,
        display: { xs: "none", md: "block" },
      }}
    />
  );
}

function CallToAction() {
  return (
    <SoftBox
      sx={{
        position: "relative",
        // overflow visible aici (la wrapper) ca decorul cu mâini să poată ieși
        // deasupra banner-ului dinspre dreapta, ca în referință.
      }}
    >
      <HandsBackgroundDecor />

      <SoftBox
        sx={{
          position: "relative",
          zIndex: 1,
          background: PANEL_BG,
          borderRadius: "16px",
          border: `1px solid ${PANEL_BORDER}`,
          boxShadow: "0 8px 28px rgba(0,0,0,0.25)",
          overflow: "hidden",
          px: { xs: 3, md: 5 },
          py: { xs: 4, md: 4.5 },
        }}
      >
      {/* Conținut: shield stânga, text mijloc, CTA dreapta */}
      <SoftBox
        sx={{
          position: "relative",
          zIndex: 1,
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "center", md: "center" },
          gap: { xs: 2.5, md: 3.5 },
          textAlign: { xs: "center", md: "left" },
        }}
      >
        {/* Shield galben outlined cu bifă */}
        <SoftBox
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <SoftBox
            sx={{
              display: { xs: "block", md: "none" },
            }}
          >
            <ShieldCheck size={72} />
          </SoftBox>
          <SoftBox
            sx={{
              display: { xs: "none", md: "block" },
            }}
          >
            <ShieldCheck size={96} />
          </SoftBox>
        </SoftBox>

        {/* Text central — flex grow ca să împingă CTA la dreapta */}
        <SoftBox
          sx={{
            flex: 1,
            minWidth: 0,
          }}
        >
          <SoftTypography
            sx={{
              color: TEXT_PRIMARY,
              fontWeight: 700,
              fontSize: { xs: "1.25rem", sm: "1.4rem", md: "1.6rem" },
              lineHeight: 1.25,
              mb: 1,
            }}
          >
            Depune o sesizare oficială
          </SoftTypography>
          <SoftTypography
            sx={{
              color: TEXT_SECONDARY,
              fontSize: { xs: "0.88rem", md: "0.95rem" },
              fontWeight: 400,
              lineHeight: 1.6,
              maxWidth: { md: "640px" },
            }}
          >
            Platforma guvernamentală îți oferă posibilitatea de a depune o sesizare oficială către
            instituțiile competente ale statului, conform procedurilor legale în vigoare.
          </SoftTypography>
        </SoftBox>

        {/* CTA galben — pe desktop la dreapta, pe mobile sub text, centrat */}
        <SoftBox
          sx={{
            flexShrink: 0,
            display: "flex",
            justifyContent: "center",
            width: { xs: "100%", md: "auto" },
            mt: { xs: 0.5, md: 0 },
          }}
        >
          <SoftButton
            component={Link}
            to="/formular-sesizare"
            sx={{
              background: `${ACCENT_YELLOW} !important`,
              color: "#0a2645 !important",
              fontWeight: 700,
              textTransform: "none",
              fontSize: { xs: "0.9rem", md: "0.95rem" },
              letterSpacing: "0.01em",
              px: { xs: 2.5, md: 3 },
              py: { xs: 1.25, md: 1.5 },
              borderRadius: "10px",
              whiteSpace: "nowrap",
              boxShadow: "0 6px 18px rgba(255,193,7,0.25)",
              "&:hover": {
                background: `${ACCENT_YELLOW_HOVER} !important`,
                color: "#0a2645 !important",
                transform: "translateY(-1px)",
                boxShadow: "0 8px 22px rgba(255,193,7,0.35)",
              },
              "&:focus, &:focus:not(:hover)": {
                background: `${ACCENT_YELLOW} !important`,
                color: "#0a2645 !important",
                boxShadow: "0 0 0 3px rgba(255,193,7,0.4) !important",
              },
              transition: "all 0.2s ease",
            }}
          >
            Completează formularul de sesizare
            <Icon sx={{ ml: 1, fontSize: "1.2rem !important" }}>arrow_forward</Icon>
          </SoftButton>
        </SoftBox>
      </SoftBox>
      </SoftBox>
    </SoftBox>
  );
}

export default CallToAction;
